import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Route, Switch, Redirect, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { Layout } from '../components';
import { ROUTES } from '../Constants';
import Job from './job';
import LoginContainer from './Login';
import Guidelines from './Guidelines';
import Dashboard from './Dashboard';
import Loading from './Loading';
import SowContainer from './Sow';
import AgentKit from './AgentKit';
import VendorPrivacyTermsContainer from './VendorPrivacyTerms';
import { refreshSessions as refreshSessionsAction } from '../lib/redux/actions/app';
import { liveAgentStatus as liveAgentStatusAction } from '../lib/redux/actions/me';

export const PrivateRoute = ({
  component: Component,
  isAuthenticated,
  hasAgentToken,
  requiresAgentToken,
  agentIsVendor,
  endJob,
  resetAgentActivity,
  ...rest
}) => (
  <Route
    {...rest}
    render={props => {
      if (!isAuthenticated) {
        return <Redirect to={{ pathname: ROUTES.LOGIN, state: { from: props.location } }} />;
      }

      if (requiresAgentToken && !hasAgentToken) {
        return <Redirect to={ROUTES.HOME} />;
      }

      return <Component endJob={endJob} resetAgentActivity={resetAgentActivity} {...props} />;
    }}
  />
);

PrivateRoute.propTypes = {
  component: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.node,
    PropTypes.func,
    PropTypes.elementType
  ]).isRequired,
  hasAgentToken: PropTypes.bool,
  isAuthenticated: PropTypes.bool.isRequired,
  agentIsVendor: PropTypes.bool,
  location: PropTypes.shape({
    hash: PropTypes.string,
    pathname: PropTypes.string,
    search: PropTypes.string,
    state: PropTypes.object
  }),
  requiresAgentToken: PropTypes.bool,

  endJob: PropTypes.func,
  resetAgentActivity: PropTypes.func
};

PrivateRoute.defaultProps = {
  hasAgentToken: false,
  agentIsVendor: null,
  location: {},
  requiresAgentToken: false,
  endJob: null,
  resetAgentActivity: null
};

export const Pages = ({
  isAuthenticated,
  refreshSessions,
  hasAgentToken,
  history,
  agentIsVendor,
  liveAgentStatus
}) => {
  const [websocketConnection, setWebsocketConnection] = useState(null);

  const endJob = async () => {
    if (!websocketConnection) {
      const connection = await liveAgentStatus();
      setWebsocketConnection(connection);
      await connection.send('EndJob');
    } else {
      await websocketConnection.send('EndJob');
    }
  };

  const resetAgentActivity = async jobId => {
    if (!websocketConnection) {
      const connection = await liveAgentStatus();
      setWebsocketConnection(connection);
      await connection.send('ResetJobActivity', jobId);
    } else {
      await websocketConnection.send('ResetJobActivity', jobId);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      refreshSessions(history);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      (async () => {
        const connection = await liveAgentStatus();
        setWebsocketConnection(connection);
      })();
    }
    return () => {
      return websocketConnection && websocketConnection.stop();
    };
  }, [isAuthenticated]);

  return (
    <Layout isAuthenticated={isAuthenticated}>
      <Switch>
        <Route path={ROUTES.LOGIN} exact component={LoginContainer} />
        <PrivateRoute
          path={ROUTES.HOME}
          isAuthenticated={isAuthenticated}
          hasAgentToken={hasAgentToken}
          agentIsVendor={agentIsVendor}
          exact
          component={Dashboard}
        />
        <PrivateRoute
          path={ROUTES.JOB}
          requiresAgentToken
          hasAgentToken={hasAgentToken}
          isAuthenticated={isAuthenticated}
          agentIsVendor={agentIsVendor}
          component={Job}
          endJob={endJob}
          resetAgentActivity={resetAgentActivity}
        />
        <PrivateRoute
          path={ROUTES.GUIDELINES}
          isAuthenticated={isAuthenticated}
          agentIsVendor={agentIsVendor}
          component={Guidelines}
        />
        <PrivateRoute
          path={ROUTES.SOW}
          isAuthenticated={isAuthenticated}
          agentIsVendor={agentIsVendor}
          component={SowContainer}
        />
        <PrivateRoute
          path={ROUTES.AGENT_KIT}
          isAuthenticated={isAuthenticated}
          agentIsVendor={agentIsVendor}
          component={AgentKit}
        />
        <PrivateRoute
          path={ROUTES.VENDOR_PRIVACY_TERMS}
          isAuthenticated={isAuthenticated}
          agentIsVendor={agentIsVendor}
          component={VendorPrivacyTermsContainer}
        />
        <PrivateRoute path={ROUTES.LOADING} isAuthenticated={isAuthenticated} component={Loading} />
      </Switch>
    </Layout>
  );
};

Pages.propTypes = {
  hasAgentToken: PropTypes.bool.isRequired,
  agentIsVendor: PropTypes.bool,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired
  }).isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  refreshSessions: PropTypes.func.isRequired,
  liveAgentStatus: PropTypes.func.isRequired
};

Pages.defaultProps = {
  agentIsVendor: null
};

function mapStateToProps({ session, agent, me }) {
  return {
    isAuthenticated: session.isAuthenticated,
    hasAgentToken: !!agent.token,
    isAvailable: me.isAvailable,
    agentIsVendor: me.agentIsVendor
  };
}

export default connect(
  mapStateToProps,
  {
    refreshSessions: refreshSessionsAction,
    liveAgentStatus: liveAgentStatusAction
  }
)(withRouter(Pages));
